import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import GlobalNav from '../components/GlobalNav';
import { LogoFull } from '../components/Logo';

const G = '#004D35';
const BG = '#f5f3ef';
const DARK = '#0a1a0f';
const BORDER = '#e8e5e0';

const CATEGORIES = [
  { name: 'CPA / Accountant', desc: 'Tax strategy, bookkeeping, and financial clarity for growing businesses.' },
  { name: 'Payroll Specialist', desc: 'Accurate, compliant payroll processing that owners can set and forget.' },
  { name: 'HR Consultant', desc: 'People strategy, compliance, and the systems that help teams scale.' },
  { name: 'Business Banker', desc: 'Lending, credit lines, and financial partnerships built for small business.' },
  { name: 'Insurance Agent', desc: 'Business coverage that protects what owners have worked hard to build.' },
  { name: 'Retirement Planner', desc: 'Long-term wealth planning for owners who want to build beyond the business.' },
];

const WHY_CARDS = [
  {
    icon: '01',
    title: 'Built for trusted expertise',
    body: 'Create a clear profile that helps business owners understand your experience and how you can help.',
  },
  {
    icon: '02',
    title: 'Reach the right owners',
    body: 'Be discoverable to small business owners looking for practical, professional guidance.',
  },
  {
    icon: '03',
    title: 'Actually verified',
    body: 'We review every application personally. Your verified badge means something — and business owners know it.',
  },
  {
    icon: '04',
    title: 'A human review',
    body: 'Every application is reviewed by our team before an advisor account is invited to join.',
  },
];

const STEPS = [
  {
    num: '1',
    title: 'Tell us about yourself',
    body: '5 minutes. Your background, your specialties, the clients you do your best work with.',
  },
  {
    num: '2',
    title: 'We review every application personally',
    body: 'No auto-approvals. Our team reviews your details and follows up on the next steps.',
  },
  {
    num: '3',
    title: 'Set up your profile and get discovered',
    body: 'Once approved, build your profile and start showing up in front of business owners who need exactly what you offer.',
  },
];

const Footer = () => (
  <footer style={{ background: '#fff', borderTop: `1px solid ${BORDER}`, padding: '32px 24px' }}>
    <div style={{ maxWidth: 1060, margin: '0 auto', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <LogoFull size={28} />
        <span style={{ fontSize: 13, color: '#aaa' }}>© 2026</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 24, fontSize: 13, color: '#888' }}>
        <Link to="/privacy" style={{ color: 'inherit', textDecoration: 'none' }}>Privacy</Link>
        <Link to="/terms" style={{ color: 'inherit', textDecoration: 'none' }}>Terms</Link>
        <Link to="/contact" style={{ color: 'inherit', textDecoration: 'none' }}>Contact</Link>
      </div>
    </div>
  </footer>
);

export default function BecomeAnAdvisor() {
  const [categories, setCategories] = useState([]);
  const [categoryError, setCategoryError] = useState('');
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [applicationResult, setApplicationResult] = useState(null);
  const [applicationError, setApplicationError] = useState('');

  const loadCategories = async () => {
    setLoadingCategories(true);
    setCategoryError('');
    try {
      const response = await fetch('/api/reference/categories');
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Unable to load advisor categories.');
      const list = Array.isArray(data) ? data : data.categories || [];
      setCategories(list.filter(item => item.slug));
    } catch (error) {
      setCategoryError(error.message || 'Unable to load advisor categories.');
    } finally { setLoadingCategories(false); }
  };

  useEffect(() => { loadCategories(); }, []);

  const handleApplication = async event => {
    event.preventDefault();
    if (submitting) return;
    const form = event.currentTarget;
    const values = new FormData(form);
    setSubmitting(true);
    setApplicationError('');
    setApplicationResult(null);
    const payload = {
      first_name: String(values.get('first_name') || '').trim(),
      last_name: String(values.get('last_name') || '').trim(),
      email: String(values.get('email') || '').trim(),
      category: String(values.get('category') || ''),
      professional_title: String(values.get('professional_title') || '').trim(),
      location: String(values.get('location') || '').trim(),
      credential_number: String(values.get('credential_number') || '').trim() || undefined,
      linkedin_url: String(values.get('linkedin_url') || '').trim() || undefined,
      website_url: String(values.get('website_url') || '').trim() || undefined,
      bio: String(values.get('bio') || '').trim() || undefined,
      website: String(values.get('website') || '').trim(),
    };
    try {
      const response = await fetch('/api/applications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      if (response.status === 429) throw new Error(data.error || 'Too many application attempts. Please wait a little and try again.');
      if (!response.ok || data.success === false || (response.status !== 200 && data.success !== true)) {
        throw new Error(data.error || 'We could not submit your application. Please check the information and try again.');
      }
      setApplicationResult({
        confirmationSent: data.confirmationSent === true,
        message: data.message || 'Your application has been received.',
      });
      form.reset();
    } catch (error) {
      setApplicationError(error.message || 'We could not submit your application. Please try again.');
    } finally { setSubmitting(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: BG, fontFamily: 'system-ui, sans-serif' }}>
      <GlobalNav page="homepage" />

      {/* HERO */}
      <section style={{ background: G, padding: '80px 24px 90px', textAlign: 'center' }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <div style={{ display: 'inline-block', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginBottom: 20, fontFamily: 'sans-serif' }}>
            For Advisors
          </div>
          <h1 style={{ fontSize: 'clamp(32px, 5vw, 52px)', fontWeight: 700, color: '#fff', fontFamily: 'Georgia, serif', lineHeight: 1.2, margin: '0 0 20px' }}>
            Your next client is already looking for you
          </h1>
          <p style={{ fontSize: 'clamp(15px, 2vw, 18px)', color: 'rgba(255,255,255,0.75)', lineHeight: 1.65, margin: '0 0 36px', fontFamily: 'sans-serif' }}>
            Viysr is where serious small business owners go to find their CPA, HR consultant, payroll specialist, and more. Get a verified profile and start getting found.
          </p>
          <a
            href="#application"
            style={{ display: 'inline-block', background: '#fff', color: G, fontWeight: 700, fontSize: 16, padding: '16px 36px', borderRadius: 999, textDecoration: 'none', fontFamily: 'sans-serif', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', transition: 'opacity 0.15s ease' }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
          >
            Apply to become an advisor
          </a>
          <div style={{ marginTop: 14, fontSize: 13, color: 'rgba(255,255,255,0.5)', fontFamily: 'sans-serif' }}>
            A short application &nbsp;·&nbsp; Reviewed by our team &nbsp;·&nbsp; No payment required to apply
          </div>
        </div>
      </section>

      <section id="application" style={{ padding: '76px 24px', background: '#eef2ec', scrollMarginTop: 76 }}>
        <div style={{ maxWidth: 760, margin: '0 auto', background: '#fff', border: `1.5px solid ${BORDER}`, borderRadius: 20, padding: 'clamp(22px, 5vw, 44px)', boxShadow: '0 12px 38px rgba(10,26,15,.06)' }}>
          <div style={{ color: G, fontSize: 11, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase', fontFamily: 'sans-serif', marginBottom: 10 }}>Advisor application</div>
          <h2 style={{ fontSize: 'clamp(26px, 4vw, 36px)', fontWeight: 700, color: DARK, fontFamily: 'Georgia,serif', margin: '0 0 10px' }}>Start with the essentials.</h2>
          <p style={{ color: '#657168', fontSize: 15, lineHeight: 1.65, margin: '0 0 26px', fontFamily: 'sans-serif' }}>Tell us what you do and where you work. If approved, you’ll receive an account invitation to finish your advisor profile.</p>
          {applicationError && <div role="alert" style={{ background: '#fff1ee', border: '1px solid #edc7bf', color: '#8e3328', padding: '12px 14px', borderRadius: 10, marginBottom: 18, fontSize: 14 }}>{applicationError}</div>}
          {applicationResult && <div role="status" style={{ background: '#edf6ef', border: '1px solid #c8dfcf', color: G, padding: '16px 18px', borderRadius: 12, marginBottom: 20, lineHeight: 1.6 }}>
            <strong>Application received.</strong> {applicationResult.message} Our team will review it. If approved, an account invitation will follow.
            {applicationResult.confirmationSent && <div style={{ marginTop: 7, fontSize: 13 }}>A receipt confirmation has also been sent to your email.</div>}
          </div>}
          {categoryError && <div role="alert" style={{ background: '#fff1ee', color: '#8e3328', padding: 12, borderRadius: 10, marginBottom: 15, fontSize: 13 }}>{categoryError} <button type="button" onClick={loadCategories} style={{ marginLeft: 8, border: 0, background: 'none', color: G, fontWeight: 700, cursor: 'pointer' }}>Retry</button></div>}
          <form onSubmit={handleApplication}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,220px),1fr))', gap: '0 14px' }}>
              {[
                ['first_name', 'First name', true, 'text'],
                ['last_name', 'Last name', true, 'text'],
                ['email', 'Email address', true, 'email'],
                ['professional_title', 'Professional title', true, 'text'],
                ['location', 'City and state', true, 'text'],
                ['credential_number', 'Credential number', false, 'text'],
                ['linkedin_url', 'LinkedIn profile URL', false, 'url'],
                ['website_url', 'Professional website', false, 'url'],
              ].map(([name, label, required, type]) => <label key={name} style={{ display: 'block', marginBottom: 16, fontSize: 12, color: '#58665d', fontWeight: 700, fontFamily: 'sans-serif' }}>
                {label}{required ? ' *' : ' · optional'}
                <input name={name} type={type} required={required} autoComplete={name === 'first_name' ? 'given-name' : name === 'last_name' ? 'family-name' : name === 'email' ? 'email' : 'off'} style={{ display: 'block', boxSizing: 'border-box', width: '100%', marginTop: 7, border: `1px solid ${BORDER}`, borderRadius: 10, background: '#fff', padding: '12px 13px', fontSize: 14, color: DARK }} />
              </label>)}
              <label style={{ display: 'block', marginBottom: 16, fontSize: 12, color: '#58665d', fontWeight: 700, fontFamily: 'sans-serif' }}>Advisor category *
                <select name="category" required disabled={loadingCategories || categories.length === 0} style={{ display: 'block', boxSizing: 'border-box', width: '100%', marginTop: 7, border: `1px solid ${BORDER}`, borderRadius: 10, background: '#fff', padding: '12px 13px', fontSize: 14, color: DARK }}>
                  <option value="">{loadingCategories ? 'Loading categories…' : 'Select a category'}</option>
                  {categories.map(category => <option key={category.id || category.slug} value={category.slug}>{category.display_name || category.name || category.label}</option>)}
                </select>
              </label>
            </div>
            <label style={{ display: 'block', marginBottom: 18, fontSize: 12, color: '#58665d', fontWeight: 700, fontFamily: 'sans-serif' }}>A little about your work <span style={{ fontWeight: 400 }}>· optional</span>
              <textarea name="bio" rows="4" maxLength="1600" placeholder="Who do you work with, and what do you help them solve?" style={{ display: 'block', boxSizing: 'border-box', width: '100%', marginTop: 7, border: `1px solid ${BORDER}`, borderRadius: 10, background: '#fff', padding: '12px 13px', fontSize: 14, color: DARK, resize: 'vertical', lineHeight: 1.55 }} />
            </label>
            <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}>
              <label>Leave this field empty<input name="website" type="text" tabIndex={-1} autoComplete="off" /></label>
            </div>
            <button type="submit" disabled={submitting || loadingCategories || categories.length === 0} style={{ background: G, color: '#fff', border: 0, borderRadius: 999, padding: '14px 25px', fontSize: 14, fontWeight: 700, cursor: submitting ? 'wait' : 'pointer', opacity: submitting || loadingCategories || categories.length === 0 ? .65 : 1 }}>
              {submitting ? 'Submitting application…' : 'Submit application'}
            </button>
            <p style={{ margin: '12px 0 0', fontSize: 12, color: '#859087', lineHeight: 1.55 }}>Applying does not create an advisor account. Account access follows approval.</p>
          </form>
        </div>
      </section>

      {/* WHY WE BUILT THIS */}
      <section style={{ padding: '80px 24px', background: '#fff' }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(24px, 4vw, 36px)', fontWeight: 700, color: DARK, fontFamily: 'Georgia, serif', margin: '0 0 24px', lineHeight: 1.25 }}>
            Great advisors shouldn't have to hustle for clients
          </h2>
          <p style={{ fontSize: 16, color: '#555', lineHeight: 1.75, margin: 0, fontFamily: 'sans-serif' }}>
            You're exceptional at what you do. But between referrals drying up, cold outreach that goes nowhere, and platforms that treat you like a commodity — growing your practice is harder than it should be.
          </p>
          <p style={{ fontSize: 16, color: '#555', lineHeight: 1.75, margin: '20px 0 0', fontFamily: 'sans-serif' }}>
            We built Viysr because small business owners deserve better access to great advisors, and great advisors deserve a better way to be found. Not a directory. Not a lead mill. A marketplace built around your expertise — where the right clients find you.
          </p>
        </div>
      </section>

      {/* WHY VIYSR — 4 CARDS */}
      <section style={{ padding: '80px 24px', background: BG }}>
        <div style={{ maxWidth: 1060, margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(24px, 4vw, 36px)', fontWeight: 700, color: DARK, fontFamily: 'Georgia, serif', margin: '0 0 40px', textAlign: 'center' }}>
            This is your unfair advantage
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 20 }}>
            {WHY_CARDS.map(({ icon, title, body }) => (
              <div key={title} style={{ background: '#fff', border: `1.5px solid ${BORDER}`, borderRadius: 16, padding: '28px 24px' }}>
                <div style={{ fontSize: 15, color: G, fontWeight: 800, letterSpacing: '.12em', marginBottom: 14 }}>{icon}</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: DARK, fontFamily: 'Georgia, serif', marginBottom: 10 }}>{title}</div>
                <p style={{ fontSize: 14, color: '#666', lineHeight: 1.65, margin: 0, fontFamily: 'sans-serif' }}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO WE'RE LOOKING FOR */}
      <section style={{ padding: '80px 24px', background: '#fff' }}>
        <div style={{ maxWidth: 1060, margin: '0 auto' }}>
          <div style={{ maxWidth: 640, marginBottom: 48 }}>
            <h2 style={{ fontSize: 'clamp(24px, 4vw, 36px)', fontWeight: 700, color: DARK, fontFamily: 'Georgia, serif', margin: '0 0 14px', lineHeight: 1.25 }}>
              We're selective. That's the point.
            </h2>
            <p style={{ fontSize: 16, color: '#666', lineHeight: 1.65, margin: 0, fontFamily: 'sans-serif' }}>
              We're currently accepting applications in six categories. If you're great at what you do and you want more of the right clients — this is for you.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
            {CATEGORIES.map(({ name, desc }) => (
              <div key={name} style={{ border: `1.5px solid ${BORDER}`, borderRadius: 14, padding: '22px 24px', display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: G, flexShrink: 0, marginTop: 5 }} />
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: DARK, fontFamily: 'Georgia, serif', marginBottom: 6 }}>{name}</div>
                  <p style={{ fontSize: 13, color: '#888', lineHeight: 1.6, margin: 0, fontFamily: 'sans-serif' }}>{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THE PROCESS — 3 STEPS */}
      <section style={{ padding: '80px 24px', background: BG }}>
        <div style={{ maxWidth: 860, margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(24px, 4vw, 36px)', fontWeight: 700, color: DARK, fontFamily: 'Georgia, serif', margin: '0 0 48px', textAlign: 'center' }}>
            A clear path from application to profile.
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {STEPS.map(({ num, title, body }, i) => (
              <div key={num} style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                  <div style={{ width: 44, height: 44, borderRadius: '50%', background: G, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: '#fff', fontFamily: 'Georgia, serif' }}>
                    {num}
                  </div>
                  {i < STEPS.length - 1 && (
                    <div style={{ width: 2, flex: 1, background: BORDER, minHeight: 40, margin: '8px 0' }} />
                  )}
                </div>
                <div style={{ paddingBottom: i < STEPS.length - 1 ? 36 : 0, paddingTop: 8 }}>
                  <div style={{ fontSize: 17, fontWeight: 700, color: DARK, fontFamily: 'Georgia, serif', marginBottom: 8 }}>{title}</div>
                  <p style={{ fontSize: 14, color: '#666', lineHeight: 1.65, margin: 0, fontFamily: 'sans-serif' }}>{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section style={{ padding: '90px 24px', background: G, textAlign: 'center' }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(26px, 4vw, 40px)', fontWeight: 700, color: '#fff', fontFamily: 'Georgia, serif', margin: '0 0 14px', lineHeight: 1.25 }}>
            You've worked hard to get good at this.
          </h2>
          <p style={{ fontSize: 18, color: 'rgba(255,255,255,0.72)', lineHeight: 1.6, margin: '0 0 36px', fontFamily: 'sans-serif' }}>
            Let's make sure the right people can find you.
          </p>
          <a
            href="#application"
            style={{ display: 'inline-block', background: '#fff', color: G, fontWeight: 700, fontSize: 16, padding: '16px 36px', borderRadius: 999, textDecoration: 'none', fontFamily: 'sans-serif', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
          >
            Start your application
          </a>
          <div style={{ marginTop: 20, fontSize: 13, color: 'rgba(255,255,255,0.45)', fontFamily: 'sans-serif' }}>
            Questions? Reach us at{' '}
            <a href="mailto:advisors@viysr.com" style={{ color: 'rgba(255,255,255,0.65)', textDecoration: 'underline' }}>
              advisors@viysr.com
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
